import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { Navigate, useParams } from 'react-router-dom'
import { CardActions, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import PasswordChecklist from 'react-password-checklist'
import styled from 'styled-components'

import InputField from 'components/form/InputField'
import SubmitButton from 'components/form/SubmitButton'
import OverlayLoader from 'components/ui/OverlayLoader'
import SuccessMessage from 'components/ui/SuccessMessage'
import { SITE_ADMIN_ROOT, SITE_USER_ROOT } from 'config'
import { selectAuth } from 'store/authentication/slice'
import { resetPassword, validateResetToken } from 'store/authentication/thunks'
import { getEntryPath } from 'store/authentication/utils'

interface ParamTypes extends ParamTypesBase {
  resetToken: string
}

export default function ResetPassword(): JSX.Element {
  const [isValidatingToken, setIsValidatingToken] = useState<boolean>(true)
  const [isValidPassword, setIsValidPassword] = useState<boolean>(false)
  const auth = useSelector(selectAuth)
  const { register, handleSubmit, watch } = useForm<ResetPasswordPayload>()
  const { ref: tokenRef } = register('token')
  const dispatch = useDispatch()
  const intl = useIntl()
  const { resetToken } = useParams<ParamTypes>()

  const { loggedIn, status, checkingLoginStatus, user } = auth

  useEffect(() => {
    if (resetToken) {
      dispatch(validateResetToken({ token: resetToken }))
    }
  }, [dispatch, resetToken])

  if (loggedIn) {
    return user.isAdmin ? (
      <Navigate to={getEntryPath() || SITE_ADMIN_ROOT} />
    ) : (
      <Navigate to={getEntryPath() || SITE_USER_ROOT} />
    )
  }

  const shouldDisplayForm = () => {
    if (isTokenInvalid()) {
      return false
    }

    return status !== 'succeeded' || isValidatingToken
  }

  const shouldDisplayPasswordValidation = () => {
    if (isTokenInvalid() || isResetDone()) {
      return false
    }

    return status !== 'loading'
  }

  const isTokenInvalid = () => {
    return status === 'failed' && !isValidatingToken
  }

  const isResetDone = () => {
    return status === 'succeeded' && !isValidatingToken
  }

  const submitForm = (data: ResetPasswordPayload) => {
    setIsValidatingToken(false)
    dispatch(resetPassword(data))
  }

  return (
    <>
      <OverlayLoader visible={checkingLoginStatus || (isValidatingToken && status === 'loading')} />

      <form onSubmit={handleSubmit(submitForm)}>
        <Typography variant={'h2'}>
          <FormattedMessage id="strings.resetPassword" />
        </Typography>

        {status === 'succeeded' && !isValidatingToken && (
          <SuccessMessage>
            <FormattedMessage id="messages.resetPasswordSuccess" />
          </SuccessMessage>
        )}

        {shouldDisplayForm() && (
          <>
            <InputField
              type="password"
              name="password"
              label={<FormattedMessage id={'forms.password'} />}
              register={register}
              required
            />

            <InputField
              type="password"
              name="passwordConfirm"
              label={<FormattedMessage id={'forms.passwordConfirm'} />}
              register={register}
              required
            />

            <input type="hidden" value={resetToken} ref={tokenRef} name="token" />
          </>
        )}

        <CardActions>
          {shouldDisplayForm() && (
            <SubmitButton
              status={status}
              text={<FormattedMessage id={'strings.resetPassword'} />}
              disabled={!isValidPassword}
            />
          )}
        </CardActions>
      </form>

      {shouldDisplayPasswordValidation() && (
        <StyledPasswordChecklist
          rules={['minLength', 'number', 'capital', 'match']}
          minLength={8}
          value={watch('password') || ''}
          valueAgain={watch('passwordConfirm') || ''}
          onChange={(isValid) => setIsValidPassword(isValid)}
          messages={{
            minLength: intl.formatMessage({ id: 'strings.passwordHasMoreThanCharacters' }, { minLength: 8 }),
            specialChar: intl.formatMessage({ id: 'strings.passwordHasSpecialCharacters' }),
            number: intl.formatMessage({ id: 'strings.passwordHasNumbers' }),
            capital: intl.formatMessage({ id: 'strings.passwordHasCapitalLetters' }),
            match: intl.formatMessage({ id: 'strings.passwordsMatch' }),
          }}
        />
      )}
    </>
  )
}

const StyledPasswordChecklist = styled(PasswordChecklist)`
  margin-top: 20px !important;
  font-size: 0.8em;
`
